/*
 * ============================================================================
 *  VARDHIX TECHNOLOGIES — SITE CONTENT CONFIG (EDIT HERE)
 * ============================================================================
 *  This single file is the content-management layer for the website.
 *  Update company details, statistics, platforms, milestones,
 *  careers and contact info here.
 * ============================================================================
 */

export const COMPANY = {
  legalName: "Vardhix Technologies Private Limited",
  shortName: "Vardhix Technologies",
  brand: "Vardhix",
  tagline: "Technology. Infrastructure. Innovation. Scale.",
  cin: "U85500PN2026PTC257484",
  registeredOffice:
    "806, Gera Imperium Rise, Hinjewadi Phase 2, Pune, Maharashtra, India",
  corporateOffice: "Pune, Maharashtra, India",
  companyType: "Private Limited Company",
  industry:
    "Information Technology, Fintech Software, Digital Platforms and Business Automation",
  email: "contact@vardhixtechnologies.com",
  website: "https://vardhixtechnologies.com",
  copyrightYear: 2026,
};

export const SOCIALS = [];

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "What We Do", to: "/what-we-do" },
  { label: "Technologies", to: "/technologies" },
  { label: "Our Platforms", to: "/platforms" },
  { label: "Growth", to: "/growth" },
  { label: "Careers", to: "/careers" },
  { label: "Contact Us", to: "/contact" },
];

export const CAPITAL_TREE = {
  name: "Capital Tree Funding",
  url: "https://capitaltreefunding.com",
  display: "capitaltreefunding.com",
};

export const HERO = {
  overline: "Vardhix Technologies Private Limited",
  headlineLines: [
    "Building Technology",
    "That Powers the Next Generation",
    "of Digital Businesses",
  ],
  sub:
    "Vardhix Technologies Private Limited develops, operates, and scales fintech platforms, trading evaluation technologies, business automation systems, and digital products designed for the rapidly evolving Indian market.",
  primaryCta: { label: "Explore Our Technologies", to: "/technologies" },
  secondaryCta: { label: "Discover Our Platforms", to: "/platforms" },
  trustLine: "Technology. Infrastructure. Innovation. Scale.",
};

// Animated statistics — EDIT figures here
export const STATS = [
  {
    value: 8,
    prefix: "₹",
    suffix: " Cr+",
    label: "Annualized Revenue Run Rate",
    note:
      "Annualized run rate based on internal company performance during the initial operating period.",
  },
  { value: 1000, prefix: "", suffix: "+", label: "Platform Users", note: "" },
  { value: 6, prefix: "", suffix: "+", label: "Technology Products", note: "" },
  { value: 100, prefix: "", suffix: "%", label: "India-Focused Infrastructure", note: "" },
  { value: 99.9, prefix: "", suffix: "%", label: "Scalable Cloud Uptime Target", note: "" },
];

export const REVENUE_STATEMENT =
  "Based on the company\u2019s internal performance figures, Vardhix Technologies and its associated platforms achieved an annualized revenue run rate of approximately \u20B98 crore within the first three months of operations.";

export const WHAT_WE_DO_CARDS = [
  {
    icon: "LineChart",
    title: "Trading Evaluation Technology",
    desc:
      "Software that powers simulated trading evaluation programs with rule-based account monitoring and analytics.",
  },
  {
    icon: "CircuitBoard",
    title: "Fintech Software Infrastructure",
    desc:
      "Custom fintech systems, transaction workflows, dashboards and financial reporting tools built for scale.",
  },
  {
    icon: "LayoutGrid",
    title: "Digital Platform Development",
    desc:
      "End-to-end web and SaaS platforms with real-time dashboards, admin panels and cloud architecture.",
  },
  {
    icon: "ShieldCheck",
    title: "Risk Management Systems",
    desc:
      "Automated risk engines, drawdown monitoring, breach detection and behavioural risk analytics.",
  },
  {
    icon: "Workflow",
    title: "Business Process Automation",
    desc:
      "Automated onboarding, notifications, workflow management and rule enforcement across operations.",
  },
  {
    icon: "BarChart3",
    title: "Data Analytics and Reporting",
    desc:
      "Trading analytics, administrative reporting and real-time insight generation at platform scale.",
  },
];

export const INFRA_NODES = [
  "Real-time market data processing",
  "Trading simulation engine",
  "Risk monitoring",
  "Account evaluation",
  "Automated rule enforcement",
  "Payout workflow management",
  "User dashboards",
  "Administrative controls",
  "Cloud infrastructure",
  "Reporting and analytics",
  "Customer support systems",
  "Fraud and abuse monitoring",
];

export const GROWTH_POINTS = [
  { icon: "TrendingUp", label: "Rapid user adoption" },
  { icon: "Server", label: "Expanding technology infrastructure" },
  { icon: "Boxes", label: "Growing product ecosystem" },
  { icon: "Rocket", label: "New platform launches" },
  { icon: "Cpu", label: "Investment in automation" },
  { icon: "ShieldCheck", label: "Continuous risk-system improvements" },
  { icon: "MapPin", label: "Nationwide market focus" },
];

export const GROWTH_STATEMENT =
  "Within its initial operating period, the company and its associated platforms achieved an annualized revenue run rate of approximately \u20B98 crore, based on internal management figures.";

// ---------------- Leadership (removed from public site) ----------------

// ---------------- Milestones ----------------
export const MILESTONES = [
  { date: "2026", title: "Company incorporated", desc: "Vardhix Technologies Private Limited is incorporated in India.", reported: false },
  { date: "2026", title: "Technology development initiated", desc: "Core engineering and platform architecture work begins.", reported: false },
  { date: "2026", title: "Capital Tree Funding launched", desc: "The first live platform goes to market.", reported: false },
  { date: "2026", title: "Initial user acquisition", desc: "Early adopters onboard across India.", reported: true },
  { date: "2026", title: "Core risk systems deployed", desc: "Automated risk monitoring and rule enforcement live.", reported: false },
  { date: "2026", title: "Trading evaluation infrastructure scaled", desc: "Systems scaled to handle growing user activity.", reported: false },
  { date: "2026", title: "Support operations expanded", desc: "Customer support and ticketing capacity increased.", reported: false },
  { date: "2026", title: "~\u20B98 Cr annualized revenue run rate reached", desc: "Company-reported figure based on internal management data.", reported: true },
  { date: "2026", title: "Additional platform concepts initiated", desc: "New products enter early development.", reported: false },
  { date: "Planned", title: "Expansion across India", desc: "Nationwide growth of the technology ecosystem.", reported: false },
];

// ---------------- Platforms ----------------
export const PLATFORMS = [
  {
    name: "Capital Tree Funding",
    status: "Live",
    url: "https://capitaltreefunding.com",
    display: "capitaltreefunding.com",
    desc:
      "An India-focused simulated trading evaluation platform providing account evaluation, risk monitoring, trader analytics, performance tracking, and technology-enabled trading environments.",
  },
  { name: "Capital3", status: "Coming Soon", desc: "Next-generation platform concept in early development.", url: "", display: "" },
  { name: "Fintech Software Products", status: "Coming Soon", desc: "Modular fintech tools for digital businesses.", url: "", display: "" },
  { name: "Business Automation Platform", status: "Coming Soon", desc: "Workflow and operations automation suite.", url: "", display: "" },
  { name: "Analytics Systems", status: "Coming Soon", desc: "Real-time analytics and reporting products.", url: "", display: "" },
  { name: "Educational Technology", status: "Coming Soon", desc: "Learning products for the Indian market.", url: "", display: "" },
];

// ---------------- Careers ----------------
export const CAREER_CATEGORIES = [
  "Software Development",
  "Backend Engineering",
  "Frontend Engineering",
  "DevOps and Cloud",
  "Product Management",
  "Data Analytics",
  "Risk Operations",
  "Customer Success",
  "Digital Marketing",
  "Finance and Compliance",
  "Business Development",
];

export const OPEN_POSITIONS = [
  { title: "Senior Backend Engineer (Python)", dept: "Backend Engineering", location: "Pune / Hybrid", type: "Full-time" },
  { title: "Frontend Engineer (React)", dept: "Frontend Engineering", location: "Pune / Remote", type: "Full-time" },
  { title: "DevOps Engineer", dept: "DevOps and Cloud", location: "Pune / Hybrid", type: "Full-time" },
  { title: "Risk Operations Analyst", dept: "Risk Operations", location: "Pune", type: "Full-time" },
  { title: "Product Manager \u2014 Fintech", dept: "Product Management", location: "Pune / Hybrid", type: "Full-time" },
  { title: "Customer Success Associate", dept: "Customer Success", location: "Pune", type: "Full-time" },
];

export const ENQUIRY_TYPES = [
  "Business Partnership",
  "Technology Solutions",
  "Platform Enquiry",
  "Media Enquiry",
  "Careers",
  "Vendor Enquiry",
  "General Enquiry",
];

export const VALUES = [
  { icon: "Lightbulb", title: "Innovation" },
  { icon: "Eye", title: "Transparency" },
  { icon: "ShieldCheck", title: "Reliability" },
  { icon: "Users", title: "User-Centric Design" },
  { icon: "TrendingUp", title: "Responsible Growth" },
  { icon: "Cpu", title: "Technology Excellence" },
  { icon: "RefreshCw", title: "Continuous Improvement" },
  { icon: "Target", title: "Operational Discipline" },
];

export const IMAGES = {
  trading:
    "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1200&q=80",
  heroBg:
    "https://images.unsplash.com/photo-1641355262431-021a396d4fe5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwZGFya3xlbnwwfHx8fDE3ODQzNzQ1MDZ8MA&ixlib=rb-4.1.0&q=85",
  infra:
    "https://images.pexels.com/photos/30820142/pexels-photo-30820142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  servers:
    "https://images.unsplash.com/photo-1752742111841-f490c48aa668?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxmaW50ZWNoJTIwaW5mcmFzdHJ1Y3R1cmUlMjB0ZWNofGVufDB8fHx8MTc4NDM3NDUwNnww&ixlib=rb-4.1.0&q=85",
  people:
    "https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxmaW50ZWNoJTIwaW5mcmFzdHJ1Y3R1cmUlMjB0ZWNofGVufDB8fHx8MTc4NDM3NDUwNnww&ixlib=rb-4.1.0&q=85",
  office:
    "https://images.pexels.com/photos/14963655/pexels-photo-14963655.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const CAPTAL_TREE_DISCLAIMER =
  "Capital Tree Funding provides simulated trading evaluation services. It is not a stockbroker, exchange, investment adviser, portfolio manager, or SEBI-registered intermediary.";
